import { Rng } from '../core/rng';
import { clamp } from '../core/math';
import { EState, Engine, makeEngines, stepEngines } from './engine';
import { FireStats, applyWater, danger, igniteBlob, stepFire } from './fire';
import { FlowField } from './flowfield';
import { Heli, HeliInput, makeHeli, stepHeli } from './heli';
import { Mountain, cellToPx, generate } from './terrain';
import { alertAll, makePeople, stepPeople } from './people';
import { carsOn, stepTraffic, updateRoadStatus } from './traffic';
import { Wind, makeWind, stepWind } from './wind';
import {
  CELL,
  Car,
  CellState,
  Drop,
  GRID_H,
  GRID_W,
  Grid,
  METRES_PER_CELL,
  PKind,
  PState,
  Particle,
  Person,
  RESOLVED,
  Road,
  TERRAIN,
  TICK,
  idx,
  makeGrid,
} from './types';
import type { Difficulty } from './difficulty';

export type Phase = 'playing' | 'over';
export type MsgKind = 'info' | 'warn' | 'good' | 'bad';

export interface Msg {
  t: number;
  text: string;
  kind: MsgKind;
}

export interface Stats {
  total: number;
  home: number;
  /** On foot, heading for a road or for the ridge. */
  fleeing: number;
  inCar: number;
  waiting: number;
  aboard: number;
  safe: number;
  rescued: number;
  lost: number;
  injured: number;
  burning: number;
  burnt: number;
  hectares: number;
  burntFrac: number;
  /** Buildings still standing, and the ones that burnt down. */
  housesTotal: number;
  housesLost: number;
  /** Engine crews caught by the fire. */
  crewLost: number;
  enginesLost: number;
}

export interface Result {
  saved: number;
  /** Never had to leave: the fire was stopped before it reached them. */
  stoodDown: number;
  lost: number;
  total: number;
  savedFrac: number;
  burntFrac: number;
  hectares: number;
  elapsed: number;
  housesSaved: number;
  housesTotal: number;
  crewLost: number;
  score: number;
  grade: string;
  gradeName: string;
  pass: boolean;
  /** What to do differently next time. */
  advice: string[];
}

// Coarse grid used for "how close is the fire" queries.
const CB = 4;
const CW = Math.ceil(GRID_W / CB);
const CH = Math.ceil(GRID_H / CB);

const MAX_PARTICLES = 900;

export class World {
  readonly grid: Grid = makeGrid();
  readonly mtn: Mountain;
  readonly roads: Road[];
  readonly wind: Wind;
  readonly heli: Heli;
  readonly people: Person[];
  readonly engines: Engine[] = [];
  /** Which engine the commander currently has selected, or -1. */
  selected = -1;
  cars: Car[] = [];
  readonly drops: Drop[] = [];
  readonly particles: Particle[] = [];
  readonly fields: { summit: FlowField; road: FlowField[] };

  readonly rng: Rng;
  private readonly fireRng: Rng;

  elapsed = 0;
  phase: Phase = 'playing';
  result: Result | null = null;
  nextCarId = 0;
  evacOrdered = false;

  input: HeliInput = { up: false, down: false, left: false, right: false, interact: false, drop: false, swap: false };

  readonly flags = {
    fullTankSaid: false,
    lowFuelSaid: false,
    roadCut: [false, false],
    firstFireSaid: false,
    ordersExplained: false,
  };

  readonly stats: Stats = {
    total: 0,
    home: 0,
    fleeing: 0,
    inCar: 0,
    waiting: 0,
    aboard: 0,
    safe: 0,
    rescued: 0,
    lost: 0,
    injured: 0,
    burning: 0,
    burnt: 0,
    hectares: 0,
    burntFrac: 0,
    housesTotal: 0,
    housesLost: 0,
    crewLost: 0,
    enginesLost: 0,
  };

  messages: Msg[] = [];
  fire: FireStats = { burning: 0, burnt: 0, intensity: 0 };

  private readonly fuelCellsAtStart: number;
  private readonly coarse = new Float32Array(CW * CH);
  private readonly bfs = new Int32Array(CW * CH);
  private readonly assembly: { x: number; y: number }[] = [];
  private assemblyIndex = 0;
  private fieldTimer = 99;
  private statusTimer = 99;
  private endTimer = 0;
  private established = false;
  private mopUp = 0;
  private smokeAcc = 0;

  constructor(
    readonly diff: Difficulty,
    readonly seed: number,
  ) {
    this.rng = new Rng(seed ^ 0x9e3779b9);
    this.fireRng = new Rng(seed ^ 0x85ebca6b);
    const genRng = new Rng(seed);
    this.mtn = generate(this.grid, genRng, diff);
    this.roads = this.mtn.roads;
    this.wind = makeWind(new Rng(seed ^ 0xc2b2ae35), diff);
    this.heli = makeHeli(cellToPx(this.mtn.base.x + this.mtn.base.w / 2), cellToPx(this.mtn.base.y + 1));
    this.people = makePeople(this.mtn.houses, genRng, diff.stubbornFrac);
    this.stats.total = this.people.length;
    this.stats.housesTotal = this.mtn.houses.length;
    this.engines.push(...makeEngines(diff, this.roads));

    let fuelCells = 0;
    for (let i = 0; i < this.grid.terrain.length; i++) if (TERRAIN[this.grid.terrain[i]].fuel > 0) fuelCells++;
    this.fuelCellsAtStart = Math.max(1, fuelCells);

    this.fields = { summit: new FlowField(), road: this.roads.map(() => new FlowField()) };
    this.buildAssemblySpots();
    this.updateRoadStatusAndTalk();
    this.updateFireDistance();
    this.recomputeFields();

    for (const i of this.mtn.ignitions) igniteBlob(this.grid, i, 1.8);
    this.say(
      `${diff.rank} on duty. ${this.people.length} people on the hill, ${diff.ignitions} fire${diff.ignitions > 1 ? 's' : ''} reported below the terraces.`,
      'warn',
    );
  }

  // -------------------------------------------------------------------------
  // Simulation
  // -------------------------------------------------------------------------

  step(): void {
    if (this.phase === 'over') return;
    const dt = TICK;
    this.elapsed += dt;

    stepWind(this.wind, this.elapsed, dt);
    this.fire = stepFire(this.grid, this.wind, this.diff, this.fireRng, dt, (_from, to) => {
      const x = (to % GRID_W) * CELL;
      const y = Math.floor(to / GRID_W) * CELL;
      this.spawnParticle(x, y, this.rng.range(-8, 8), this.rng.range(-20, -6), 0.8, PKind.Ember);
    });

    this.stepDrops(dt);

    this.statusTimer += dt;
    if (this.statusTimer >= 0.5) {
      this.statusTimer = 0;
      this.updateRoadStatusAndTalk();
      this.updateFireDistance();
    }

    this.fieldTimer += dt;
    if (this.fieldTimer >= 1.0) {
      this.fieldTimer = 0;
      this.recomputeFields();
    }

    stepPeople(this, dt);
    stepTraffic(this, dt);
    stepEngines(this, dt);
    stepHeli(this, this.input, dt);
    this.stepParticles(dt);

    this.stats.burning = this.fire.burning;
    this.stats.burnt = this.fire.burnt;
    this.stats.housesLost = this.mtn.houses.reduce(
      (a, h) => a + (this.grid.state[idx(h.x + 1, h.y + 1)] === CellState.Burnt ? 1 : 0),
      0,
    );
    this.stats.burntFrac = this.fire.burnt / this.fuelCellsAtStart;
    this.stats.hectares = (this.fire.burnt * METRES_PER_CELL * METRES_PER_CELL) / 10000;

    if (!this.flags.firstFireSaid && this.fire.burning > 40) {
      this.flags.firstFireSaid = true;
      this.say('The fire is now burning strongly and spreading up the barranco, the steep gully.', 'bad');
    }

    this.checkEnd(dt);
  }

  /**
   * The incident is over when the fire is out. Anyone still on the hill at that
   * point simply stood down and stayed put, which counts as saved. If instead
   * everybody has already left, we keep running for a mop-up window (the time
   * spent putting out what is left) rather than freezing the burnt-area score
   * the moment the last civilian is clear.
   */
  private checkEnd(dt: number): void {
    const s = this.stats;
    const unresolved = s.total - (s.safe + s.rescued + s.lost);
    if (this.fire.burning > 25) this.established = true;

    // A fire that has only just started is also a small fire, so only an
    // established one can be declared out, and never in the first 45 seconds.
    if (this.fire.burning === 0 || (this.established && this.fire.burning <= 3)) {
      this.endTimer += dt;
      if (this.endTimer > (this.fire.burning === 0 ? 10 : 30) && this.elapsed > 45) {
        this.finish();
        return;
      }
    } else {
      this.endTimer = 0;
    }

    if (unresolved === 0) {
      this.mopUp += dt;
      if ((this.fire.burning <= 25 && this.mopUp > 40) || this.mopUp > 400) {
        this.finish();
        return;
      }
    }

    if (this.elapsed > 2400) this.finish();
  }

  private finish(): void {
    const s = this.stats;
    // Everyone not already accounted for when the fire went out survived it.
    let stoodDown = 0;
    for (const p of this.people) {
      if (RESOLVED(p.state)) continue;
      p.state = PState.Safe;
      stoodDown++;
    }
    s.safe += stoodDown;
    s.home = 0;
    s.fleeing = 0;
    s.inCar = 0;
    s.waiting = 0;
    s.aboard = 0;
    const saved = s.safe + s.rescued;
    const savedFrac = s.total > 0 ? saved / s.total : 0;
    const burntFrac = s.burntFrac;
    const housesSaved = s.housesTotal - s.housesLost;
    const score = Math.max(
      0,
      Math.round(
        saved * 120 -
          s.lost * 80 +
          s.rescued * 30 +
          (1 - burntFrac) * 600 +
          housesSaved * 25 -
          s.crewLost * 150,
      ),
    );
    // The standard is everybody. There is no percentage to clear. The grade
    // also says how much of the mountain and how many buildings you kept.
    const everyone = s.lost === 0;
    let grade = 'F';
    let gradeName = 'Catástrofe (a disaster)';
    if (everyone) {
      if (burntFrac <= this.diff.maxBurnA && s.housesLost === 0) {
        grade = 'A';
        gradeName = 'Sobresaliente (outstanding)';
      } else if (burntFrac <= this.diff.maxBurnA) {
        grade = 'B';
        gradeName = 'Notable (very good)';
      } else if (burntFrac <= this.diff.maxBurnB) {
        grade = 'C';
        gradeName = 'Aprobado (a pass)';
      } else {
        grade = 'D';
        gradeName = 'Todos a salvo (everybody alive)';
      }
    } else if (s.lost <= 2) {
      grade = 'E';
      gradeName = 'Insuficiente (not good enough)';
    }
    // Losing a crew never earns a top grade, whatever else went right.
    if (s.crewLost > 0 && (grade === 'A' || grade === 'B')) {
      grade = 'C';
      gradeName = 'Aprobado · con bajas (a pass, but you lost a crew)';
    }
    this.result = {
      saved,
      stoodDown,
      lost: s.lost,
      total: s.total,
      savedFrac,
      burntFrac,
      hectares: s.hectares,
      elapsed: this.elapsed,
      housesSaved,
      housesTotal: s.housesTotal,
      crewLost: s.crewLost,
      score,
      grade,
      gradeName,
      pass: everyone,
      advice: this.afterActionAdvice(everyone, burntFrac),
    };
    this.phase = 'over';
  }

  // -------------------------------------------------------------------------
  // Coaching
  // -------------------------------------------------------------------------

  /**
   * What to do right now to get everybody off the mountain. The target is
   * always all of them, so instead of a percentage the game lists what is
   * stopping you. The list is cut to five, so the urgent ones are sorted to the
   * top first: otherwise a late but urgent tip can fall off the end.
   */
  tips(): { text: string; urgent: boolean }[] {
    const out: { text: string; urgent: boolean }[] = [];
    const s = this.stats;
    const h = this.heli;

    const trapped = this.people.filter(
      (p) => !RESOLVED(p.state) && p.state !== PState.Aboard && p.health < 0.9,
    ).length;
    if (trapped > 0) out.push({ text: `${trapped} ${trapped === 1 ? 'person is' : 'people are'} being caught by the fire. Nothing else matters as much. Drop water between the fire and them.`, urgent: true });

    if (s.waiting > 0 && h.mode === 'bucket')
      out.push({ text: `${s.waiting} waiting at the assembly point and you are carrying the water bucket. Land at the base pad and press B to fit seats instead.`, urgent: s.waiting > 3 });

    if (s.waiting > 0 && h.mode === 'seats' && h.aboard.length === 0)
      out.push({ text: `${s.waiting} are waiting at the white H, the assembly point. Land there and hold E to load them.`, urgent: false });

    if (h.aboard.length > 0)
      out.push({ text: `${h.aboard.length} aboard. Land at the base pad in the valley and let them out. They are not safe until you do.`, urgent: false });

    if (!this.evacOrdered && s.home > 0 && this.fire.burning > 20)
      out.push({ text: `${s.home} still indoors and the fire is spreading. Press V to order everyone out. Wait much longer and they will have to leave on foot.`, urgent: s.home > s.total * 0.5 });

    for (const road of this.roads) {
      if (road.cut !== null && s.inCar > 0)
        out.push({ text: `${road.name} is cut by fire. Anyone on it will leave their car and walk uphill. The assembly point is now their only way out.`, urgent: true });
    }

    const idle = this.engines.filter((e) => e.state === EState.Staging && !e.order).length;
    if (idle > 0)
      out.push({ text: `${idle} ${idle === 1 ? 'fire engine is' : 'fire engines are'} parked with no orders. Press P and send them to a village in the fire's path.`, urgent: false });

    const warned = this.engines.filter((e) => e.warned).length;
    if (warned > 0)
      out.push({
        text: `${warned === 1 ? 'A crew has' : `${warned} crews have`} reported the fire closing on them. Order them to hold, or let them pull out. Do not ignore it.`,
        urgent: true,
      });

    if (h.mode === 'bucket' && h.water < 0.05 && this.fire.burning > 30)
      out.push({ text: 'Bucket empty. Fly to the Embalse de Béznar, the reservoir at the bottom left, and hold E to fill it.', urgent: false });

    if (h.fuel < 0.25)
      out.push({ text: 'Fuel below a quarter. Fly back to the base pad before it runs out and you are stuck on the ground.', urgent: h.fuel < 0.12 });

    if (out.length === 0 && this.fire.burning > 0)
      out.push({ text: 'Nobody is in immediate danger. Now drop water on the flanks of the fire, its sides rather than its leading edge, and save as much of the mountain as you can.', urgent: false });

    // Stable sort, so within urgent and within calm the authored order holds.
    return out.map((t, i) => ({ t, i }))
      .sort((a, b) => Number(b.t.urgent) - Number(a.t.urgent) || a.i - b.i)
      .map((x) => x.t)
      .slice(0, 5);
  }

  /** What to do differently next time, based on how this one actually went. */
  private afterActionAdvice(everyone: boolean, burntFrac: number): string[] {
    const s = this.stats;
    const out: string[] = [];
    if (!everyone) {
      out.push(
        'Everyone is meant to get off the mountain. The people who die are almost always the ones still walking when a road closes. Press V to order the evacuation earlier, and fit the seats before they reach the ridge.',
      );
    }
    if (s.crewLost > 0)
      out.push('You lost a crew. When a fire engine reports that its escape route is threatened, pull it out. An engine caught by the fire cannot help anyone.');
    if (s.housesLost > 0)
      out.push(
        `${s.housesLost} ${s.housesLost === 1 ? 'building' : 'buildings'} burned down. Send a fire engine to Protect a village before the fire gets there. Once the fire arrives, it is too late.`,
      );
    if (burntFrac > this.diff.maxBurnB)
      out.push('Most of the mountain burned. Water dropped on the head of the fire, its fast leading edge, is wasted. Drop on the flanks, its slower sides, to make the fire narrower. Use the wind forecast to work out where it will go next.');
    else if (burntFrac > this.diff.maxBurnA)
      out.push('Dropping closer to the fire would raise the grade. Wet ground stays wet for nearly a minute, so a line of drops just ahead of the flames will stop them there.');
    if (out.length === 0)
      out.push('Everybody got off the mountain and most of it did not burn. There is nothing to change.');
    return out;
  }

  // -------------------------------------------------------------------------
  // Player orders
  // -------------------------------------------------------------------------

  orderEvacuation(): void {
    if (this.evacOrdered) return;
    this.evacOrdered = true;
    alertAll(this);
  }

  // -------------------------------------------------------------------------
  // Helpers used by the subsystems
  // -------------------------------------------------------------------------

  say(text: string, kind: MsgKind = 'info'): void {
    // Check the last few lines, not just the most recent one: with several
    // units reporting, a repeat is rarely the line immediately before it.
    for (let i = this.messages.length - 1; i >= 0 && i > this.messages.length - 8; i--) {
      const m = this.messages[i];
      if (this.elapsed - m.t > 12) break;
      if (m.text === text) return;
    }
    this.messages.push({ t: this.elapsed, text, kind });
    if (this.messages.length > 80) this.messages.shift();
  }

  spawnParticle(x: number, y: number, vx: number, vy: number, life: number, kind: PKind): void {
    if (this.particles.length >= MAX_PARTICLES) return;
    this.particles.push({ x, y, vx, vy, life, max: life, kind, seed: this.rng.int(0, 255) });
  }

  carsOnRoad(id: number): number {
    return carsOn(this, id);
  }

  /** Distance in cells from (x, y) to the nearest fire, from the coarse field. */
  fireDistance(x: number, y: number): number {
    const bx = clamp(Math.floor(x / CB), 0, CW - 1);
    const by = clamp(Math.floor(y / CB), 0, CH - 1);
    return this.coarse[by * CW + bx] * CB;
  }

  nextAssemblySpot(): { x: number; y: number } {
    const spot = this.assembly[this.assemblyIndex % this.assembly.length];
    this.assemblyIndex++;
    return spot;
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private stepDrops(dt: number): void {
    for (let k = this.drops.length - 1; k >= 0; k--) {
      const d = this.drops[k];
      d.t += dt;
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      if (d.t < d.fall) continue;
      const cx = clamp(Math.round(d.x / CELL), 0, GRID_W - 1);
      const cy = clamp(Math.round(d.y / CELL), 0, GRID_H - 1);
      const doused = applyWater(this.grid, cx, cy, d.radius, d.amount);
      for (let n = 0; n < 26; n++) {
        const a = this.rng.range(0, Math.PI * 2);
        const r = this.rng.range(0, d.radius * CELL);
        this.spawnParticle(
          d.x + Math.cos(a) * r * 0.4,
          d.y + Math.sin(a) * r * 0.3,
          Math.cos(a) * 26,
          Math.sin(a) * 14 - 8,
          this.rng.range(0.35, 0.8),
          PKind.Splash,
        );
      }
      if (doused > 0) {
        for (let n = 0; n < Math.min(30, doused * 2); n++) {
          this.spawnParticle(
            d.x + this.rng.range(-d.radius, d.radius) * CELL,
            d.y + this.rng.range(-d.radius, d.radius) * CELL * 0.6,
            this.rng.range(-6, 6),
            this.rng.range(-26, -10),
            this.rng.range(0.8, 1.6),
            PKind.Steam,
          );
        }
        this.say(`Good drop. ${doused} cells put out.`, 'good');
      }
      this.drops.splice(k, 1);
    }
  }

  private stepParticles(dt: number): void {
    const w = this.wind;
    for (let k = this.particles.length - 1; k >= 0; k--) {
      const p = this.particles[k];
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(k, 1);
        continue;
      }
      switch (p.kind) {
        case PKind.Smoke:
          p.vx += (w.ux * w.speed * 42 - p.vx) * dt * 1.4;
          p.vy += (w.uy * w.speed * 42 - 16 - p.vy) * dt * 1.4;
          break;
        case PKind.Ember:
          p.vx += w.ux * w.speed * 60 * dt;
          p.vy += w.uy * w.speed * 60 * dt + 26 * dt;
          break;
        case PKind.Splash:
          p.vy += 120 * dt;
          break;
        case PKind.Steam:
          p.vy -= 22 * dt;
          p.vx += (w.ux * w.speed * 26 - p.vx) * dt;
          break;
        case PKind.Cheer:
          p.vy -= 12 * dt;
          break;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }

    // Smoke from the fire itself, sampled rather than iterated.
    this.smokeAcc += dt * Math.min(70, 6 + this.fire.intensity * 1.4);
    while (this.smokeAcc >= 1) {
      this.smokeAcc -= 1;
      if (this.fire.burning === 0) break;
      for (let tries = 0; tries < 12; tries++) {
        const i = this.rng.int(0, this.grid.state.length - 1);
        if (this.grid.state[i] !== CellState.Burning) continue;
        const x = (i % GRID_W) * CELL + this.rng.range(0, CELL);
        const y = Math.floor(i / GRID_W) * CELL + this.rng.range(0, CELL);
        this.spawnParticle(x, y, this.rng.range(-6, 6), this.rng.range(-30, -12), this.rng.range(1.6, 3.4), PKind.Smoke);
        break;
      }
    }
  }

  private updateRoadStatusAndTalk(): void {
    updateRoadStatus(this);
    for (const road of this.roads) {
      const cut = road.cut !== null;
      if (cut && !this.flags.roadCut[road.id]) {
        this.flags.roadCut[road.id] = true;
        this.say(`${road.name} is CUT by fire.`, 'bad');
      } else if (!cut && this.flags.roadCut[road.id]) {
        this.flags.roadCut[road.id] = false;
        this.say(`${road.name} is open again.`, 'good');
      }
    }
  }

  private recomputeFields(): void {
    const grid = this.grid;
    const walkCost = (i: number): number => {
      const def = TERRAIN[grid.terrain[i]];
      if (!Number.isFinite(def.walk)) return Infinity;
      let c = def.walk;
      if (grid.state[i] === CellState.Burning) c += 70;
      else {
        const x = i % GRID_W;
        const y = (i - x) / GRID_W;
        c += danger(grid, x, y) * 10;
      }
      return c;
    };

    const padTargets: number[] = [];
    const pad = this.mtn.helipad;
    for (let y = pad.y; y < pad.y + pad.h; y++)
      for (let x = pad.x; x < pad.x + pad.w; x++) padTargets.push(idx(x, y));
    this.fields.summit.compute(padTargets, walkCost);

    this.roads.forEach((road, k) => {
      const targets: number[] = [];
      road.cells.forEach((c, s) => {
        // Only aim people at stretches they could actually drive out from.
        if (!Number.isFinite(road.blocked[s])) targets.push(idx(c.x, c.y));
      });
      this.fields.road[k].compute(targets, walkCost);
    });
  }

  private updateFireDistance(): void {
    this.coarse.fill(Infinity);
    let head = 0;
    let tail = 0;
    for (let i = 0; i < this.grid.state.length; i++) {
      if (this.grid.state[i] !== CellState.Burning) continue;
      const x = i % GRID_W;
      const y = (i - x) / GRID_W;
      const b = Math.floor(y / CB) * CW + Math.floor(x / CB);
      if (this.coarse[b] === 0) continue;
      this.coarse[b] = 0;
      this.bfs[tail++] = b;
    }
    while (head < tail) {
      const b = this.bfs[head++];
      const bx = b % CW;
      const by = (b - bx) / CW;
      const d = this.coarse[b] + 1;
      for (let dy = -1; dy <= 1; dy++)
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = bx + dx;
          const ny = by + dy;
          if (nx < 0 || ny < 0 || nx >= CW || ny >= CH) continue;
          const nb = ny * CW + nx;
          if (d < this.coarse[nb]) {
            this.coarse[nb] = d;
            if (tail < this.bfs.length) this.bfs[tail++] = nb;
          }
        }
    }
  }

  private buildAssemblySpots(): void {
    const pad = this.mtn.helipad;
    // Ring outward from just outside the touchdown area so the pad stays clear.
    for (let ring = 0; ring < 8; ring++) {
      for (let x = pad.x - 3 - ring; x <= pad.x + pad.w + 2 + ring; x++) {
        for (const y of [pad.y - 2 - ring, pad.y + pad.h + 1 + ring]) {
          if (x < 1 || x >= GRID_W - 1 || y < 1 || y >= GRID_H - 1) continue;
          if (TERRAIN[this.grid.terrain[idx(x, y)]].walk === Infinity) continue;
          this.assembly.push({ x: x + 0.5, y: y + 0.5 });
        }
      }
    }
    if (this.assembly.length === 0) this.assembly.push({ x: pad.x + 0.5, y: pad.y + 0.5 });
  }
}


